import {
  setPlumaContextValue,
  setPlumaTheme,
  setPlumaIconManager,
  plumaContextValueStore,
  plumaThemeStore,
  plumaIconManagerStore,
} from '@customerio/hydra-pluma-state';
import { IconManager } from '@customerio/pluma-icons';
import { defaultTheme } from '@customerio/pluma-components/css';
import { PlumaProvider } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { forwardRef } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import type { LinkComponent, PlumaProviderProps } from '@customerio/pluma-components/react';
import type { TargetTransitionOptions } from '@/context/safe-navigate';
import { useSafeNavigate } from '@/context/safe-navigate';
import { shouldProcessLinkClick } from '../../utils/navigation';

setPlumaContextValue({
  componentFlags: {
    unsafe_useLegacyButton: true,
    unsafe_useLegacyTextField: true,
    unsafe_useLegacyTextArea: true,
    unsafe_useLegacyLink: true,
  },
});
setPlumaTheme(defaultTheme);
const iconManager = new IconManager();
setPlumaIconManager(iconManager);

/**
 * PlumaLinkDefinition connects the Pluma design system to the
 * react-router-dom Link component. It allows for the use of
 * react-router-dom's navigation features while still using
 * Pluma's styling and theming.
 */
const PlumaLinkDefinition = forwardRef(function (props, ref) {
  const { href, replace, ...restProps } = props;

  const safeNavigate = useSafeNavigate();

  const onLinkClick = (event: React.MouseEvent<HTMLAnchorElement>, opts?: TargetTransitionOptions) => {
    if (!shouldProcessLinkClick(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    safeNavigate(event.currentTarget.getAttribute('href') ?? '', opts);
  };

  return <ReactRouterLink to={href ?? ''} replace={replace} {...restProps} onClick={onLinkClick} ref={ref} />;
}) as LinkComponent;

/**
 * Configures Pluma for Root and EmbedRoot components,
 * set globally so that other apps can consume our configuration.
 */
export function ConnectedPlumaProvider(props: PlumaProviderProps) {
  const plumaContextValue = useStore(plumaContextValueStore);
  const plumaTheme = useStore(plumaThemeStore);
  const plumaIconManager = useStore(plumaIconManagerStore);

  return (
    <PlumaProvider
      theme={plumaTheme ?? defaultTheme}
      iconManager={plumaIconManager ?? iconManager}
      componentFlags={plumaContextValue?.componentFlags}
      shouldApplyCssIsolation={true}
      linkComponent={PlumaLinkDefinition}
      {...props}
    />
  );
}
